import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { renderAsync } from 'docx-preview';
import { apiService } from '../services/api';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 1100px;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
  
  mark {
    background-color: #ffeb3b;
    padding: 0.2em 0;
    animation: highlight-pulse 2s ease-in-out;
  }
  
  .highlight-text {
    background-color: #ffeb3b;
    animation: highlight-pulse 2s ease-in-out;
    border-radius: 4px;
    padding: 0.5rem;
  }
  
  @keyframes highlight-pulse {
    0% { background-color: #ffeb3b; }
    50% { background-color: #ffd700; }
    100% { background-color: #ffeb3b; }
  }
  
  /* Table styling */
  table {
    border-collapse: collapse;
    width: 100%;
    margin: 1rem 0;
    font-size: 0.9rem;
  }
  
  th, td {
    border: 1px solid #ddd;
    padding: 0.75rem;
    text-align: left;
  }
  
  th {
    background-color: #f5f5f5;
    font-weight: 600;
    color: #333;
  }
  
  tr:nth-child(even) {
    background-color: #fafafa;
  }
  
  tr:hover {
    background-color: #f0f0f0;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #888;
  
  &:hover {
    color: #333;
  }
`;

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  content?: string;
  manualType?: string;
  highlightText?: string;
}

const stripMarkdown = (text: string): string =>
  text
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/_(.+?)_/g, '$1')
    .replace(/~~(.+?)~~/g, '$1')
    .replace(/`(.+?)`/g, '$1')
    .replace(/\[(.+?)\]\(.+?\)/g, '$1')
    .replace(/!\[.*?\]\(.+?\)/g, '')
    .replace(/^#{1,6}\s+(.+)$/gm, '$1')
    .replace(/^\s*[-*+]\s+/gm, '')
    .replace(/^\s*\d+\.\s+/gm, '')
    .replace(/<[^>]+>/g, '')
    .replace(/\\(.)/g, '$1')
    .replace(/[ \t]+/g, ' ')
    .split('\n').map(line => line.trim()).join('\n')
    .trim();

const normalizeForSearch = (str: string) =>
  stripMarkdown(str)
    .replace(/\s+/g, ' ')
    .replace(/\s+([,.:;!?])/g, '$1')
    .trim();

const applyHighlight = (container: HTMLElement, highlightText: string) => {
  container.querySelectorAll('.highlight-text').forEach(el =>
    el.classList.remove('highlight-text')
  );

  const normalizedSearch = normalizeForSearch(highlightText);
  if (!normalizedSearch) return;

  const allBlocks = Array.from(
    container.querySelectorAll('p, ul, ol, li, h1, h2, h3, h4, h5, h6, blockquote')
  ) as HTMLElement[];

  let bestMatch: HTMLElement[] = [];
  let bestMatchLength = Infinity;

  for (let i = 0; i < allBlocks.length; i++) {
    for (let windowSize = 1; windowSize <= Math.min(10, allBlocks.length - i); windowSize++) {
      const slice = allBlocks.slice(i, i + windowSize);
      const parents = slice.map(el => el.parentElement);
      if (!parents.every(p => p === parents[0])) continue;

      const combined = normalizeForSearch(
        slice.map(el => el.innerText || el.textContent || '').join(' ')
      );
      if (combined.includes(normalizedSearch) && combined.length < bestMatchLength) {
        bestMatch = slice;
        bestMatchLength = combined.length;
      }
    }
  }

  if (bestMatch.length > 0) {
    bestMatch.forEach((el, idx) => {
      el.classList.add('highlight-text');
      if (idx === 0) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  } else {
    console.warn('Could not find highlight text in rendered document:', highlightText);
  }
};

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, manualType, highlightText }) => {
  const docxContainerRef = useRef<HTMLDivElement>(null);
  const renderedManualType = useRef<string | null>(null);

  // Load and render the DOCX only when the manual type changes (or first open).
  useEffect(() => {
    if (!isOpen || !manualType) return;
    if (renderedManualType.current === manualType) return;

    let cancelled = false;

    const loadAndRender = async () => {
      try {
        const blob = await apiService.getOriginalDocx(manualType);
        if (cancelled || !docxContainerRef.current) return;

        docxContainerRef.current.innerHTML = '';
        await renderAsync(blob, docxContainerRef.current, undefined, {
          inWrapper: false,
          ignoreWidth: true,
        });

        if (cancelled) return;
        renderedManualType.current = manualType;

        if (highlightText && docxContainerRef.current) {
          applyHighlight(docxContainerRef.current, highlightText);
        }
      } catch (e) {
        console.error('Failed to load or render DOCX:', e);
      }
    };

    loadAndRender();
    return () => { cancelled = true; };
  }, [isOpen, manualType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-apply highlighting whenever highlightText changes without re-fetching.
  useEffect(() => {
    if (!isOpen || !highlightText || !docxContainerRef.current) return;
    if (renderedManualType.current !== manualType) return;
    applyHighlight(docxContainerRef.current, highlightText);
  }, [highlightText, isOpen, manualType]);

  // Reset the rendered-type tracker when the modal closes so the next open
  // always fetches a fresh (possibly updated) document from the server.
  useEffect(() => {
    if (!isOpen) renderedManualType.current = null;
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContent>
        <CloseButton onClick={onClose}>&times;</CloseButton>
        <div ref={docxContainerRef} />
      </ModalContent>
    </ModalOverlay>
  );
};

export default Modal;
